import React from 'react';

interface AddressProperty {
  name: string;
  label: string;
  configurable: boolean;
}

interface AddressPropertyGroup {
  name: string;
  properties: AddressProperty[];
}

interface ApiKeyConfigurationTabProps {
  selectedFields: string[];
  onCheckboxChange: (propertyName: string) => void;
  onSave: () => Promise<void>;
  isLoading: boolean;
}

const addressPropertyGroups: AddressPropertyGroup[] = [
  {
    name: "Core Address Components",
    properties: [
      { name: "street1", label: "Street 1", configurable: true },
      { name: "street2", label: "Street 2", configurable: true },
      { name: "city", label: "City", configurable: true },
      { name: "zip", label: "ZIP", configurable: true },
      { name: "stateCode", label: "State CODE", configurable: true },
      { name: "zipSuffix", label: "ZIP Suffix", configurable: true },
      { name: "country", label: "Country", configurable: true },
    ]
  },
  {
    name: "Coordinates & Location Data",
    properties: [
      { name: "latitude", label: "Latitude", configurable: false },
      { name: "coordinateSource", label: "Coordinate Source", configurable: true },
      { name: "longitude", label: "Longitude", configurable: false },
    ]
  },
  {
    name: "Google Validation Service",
    properties: [
      { name: "isGoogleValid", label: "Is Google Valid", configurable: true },
      { name: "googleFormattedAddress", label: "Google Formatted Address", configurable: true },
      { name: "googlePlaceId", label: "Google Place ID", configurable: true },
      { name: "deliveryTime", label: "Delivery Time (seconds)", configurable: true },
      { name: "googlePossibleNextAction", label: "Google Possible Next Action", configurable: true },
      { name: "googleLatitude", label: "Google Latitude", configurable: true },
      { name: "googleLongitude", label: "Google Longitude", configurable: true },
    ]
  },
  {
    name: "Radar Validation Service",
    properties: [
      { name: "isRadarValid", label: "Is Radar Valid", configurable: true },
      { name: "radarFormattedAddress", label: "Radar Formatted Address", configurable: true },
      { name: "radarLatitude", label: "Radar Latitude", configurable: true },
      { name: "radarLongitude", label: "Radar Longitude", configurable: true },
      { name: "radarPlaceId", label: "Radar Place ID", configurable: true },
      { name: "radarGeoconfidence", label: "Radar Geoconfidence", configurable: true },
      { name: "radarVerificationStatus", label: "Radar Verification Status", configurable: true },
    ]
  },
  {
    name: "Address Classification & Type",
    properties: [
      { name: "destinationType", label: "Destination Type (USPS)", configurable: true },
      { name: "isApartment", label: "Is Apartment", configurable: true },
      { name: "validationSource", label: "Validation Source", configurable: true },
    ]
  },
  {
    name: "Quality & Confidence Metrics",
    properties: [
      { name: "validationFeedback", label: "Validation Feedback", configurable: true },
      { name: "geoFenceConfidence", label: "GeoFence Confidence", configurable: true },
    ]
  },
  {
    name: "System Fields & Metadata",
    properties: [
      { name: "id", label: "Id", configurable: true },
      { name: "isValid", label: "Is Valid", configurable: false },
      { name: "traceId", label: "Trace ID", configurable: false },
      { name: "status", label: "Status", configurable: false },
    ]
  }
];

const ApiKeyConfigurationTab: React.FC<ApiKeyConfigurationTabProps> = ({
  selectedFields,
  onCheckboxChange,
  onSave,
  isLoading
}) => {
  const splitIntoColumns = (items: AddressProperty[]) => {
    const midpoint = Math.ceil(items.length / 2);
    return [items.slice(0, midpoint), items.slice(midpoint)];
  };

  return (
    <div className="card border-0 shadow-sm mb-5">
      <div className="card-body">
        {addressPropertyGroups.map((group, groupIndex) => (
          <div key={group.name} className="mb-3">
            <h6 className="mb-2 text-muted fw-semibold">{group.name}</h6>
            <div className="row g-2">
              {splitIntoColumns(group.properties).map((column, colIndex) => (
                <div key={colIndex} className="col-6">
                  <div className="vstack gap-1">
                    {column.map((prop) => (
                      <div key={prop.name} className="form-check form-check-sm">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={prop.configurable ? selectedFields.includes(prop.name) : true}
                          onChange={() => prop.configurable && onCheckboxChange(prop.name)}
                          disabled={!prop.configurable}
                          id={`check-${prop.name}`}
                        />
                        <label
                          className="form-check-label small"
                          htmlFor={`check-${prop.name}`}
                        >
                          {prop.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {groupIndex < addressPropertyGroups.length - 1 && (
              <hr className="my-3" />
            )}
          </div>
        ))}
      </div>
      <div className="card-footer bg-white border-top">
        <div className="d-flex justify-content-end">
          <button
            type="button"
            className="btn btn-primary"
            onClick={onSave}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Saving...
              </>
            ) : (
              "Save Configuration"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApiKeyConfigurationTab;
